
import { api } from '../core';
import { Conversation, Message } from '../../types';
import { MOCK_CONVERSATIONS } from '../mockData';
import { localDB } from '../helpers';

// Helper to map API Response (RequestOutputVM) to UI Message
const mapRequestToMessages = (req: any): Message[] => {
    const messages: Message[] = [];
    
    // User Message (Input)
    if (req.input) {
        messages.push({
            id: `${req.id}_user`,
            role: 'user',
            content: req.input,
            timestamp: new Date(req.startedAt || req.createdAt).getTime()
        });
    }
    
    // Assistant Message (Result)
    if (req.result) {
        messages.push({
            id: `${req.id}_assistant`,
            role: 'assistant',
            content: req.result,
            timestamp: new Date(req.completedAt || req.updatedAt || Date.now()).getTime()
        });
    }
    
    return messages;
};

export const ChatService = {
  getHistory: async (): Promise<Conversation[]> => {
    return api.execute<any[]>(
      '/api/V1/Request/GetAllByLanguage',
      async () => localDB.get<Conversation[]>('lahja_conversations', MOCK_CONVERSATIONS),
      { mockDelay: 700 }
    ).then(res => {
        // Local/Mock fallback check
        if (!Array.isArray(res) || (res.length > 0 && 'messages' in res[0])) {
            return res as Conversation[];
        }

        // Server Data Mapping
        // Group Requests by 'correlationId' (acting as Conversation ID)
        const conversationsMap: Record<string, Conversation> = {};
        
        res.forEach((req: any) => {
            // Only process chat type requests
            if (req.type !== 'chat' && req.type !== 'message' && !req.input) return;

            const conversationId = req.correlationId || req.id;
            
            if (!conversationsMap[conversationId]) {
                conversationsMap[conversationId] = {
                    id: conversationId,
                    title: req.input ? req.input.substring(0, 40) : 'New Conversation',
                    updatedAt: new Date(req.updatedAt || req.createdAt).getTime(),
                    dialect: 'najdi', // Default, dialect is often stored in Agent config
                    messages: []
                };
            }
            
            conversationsMap[conversationId].messages.push(...mapRequestToMessages(req));
        });

        // Sort messages within conversations
        Object.values(conversationsMap).forEach(conv => {
            conv.messages.sort((a, b) => a.timestamp - b.timestamp);
        });

        // Return sorted conversations (newest first)
        return Object.values(conversationsMap).sort((a, b) => b.updatedAt - a.updatedAt);
    });
  },

  saveConversation: async (conversation: Conversation): Promise<Conversation> => {
    // In server mode, conversations are implicitly saved via Requests. 
    // This mostly supports the local DB fallback.
    return api.execute<Conversation>(
      `/api/mock/conversation/${conversation.id}`, // Virtual endpoint
      async () => {
        localDB.updateItem('lahja_conversations', conversation, []);
        return conversation;
      },
      { method: 'PUT', body: conversation }
    );
  },

  sendMessage: async (conversationId: string, message: string): Promise<Message> => {
      // Map to RequestCreateVM
      const payload = {
          correlationId: conversationId,
          input: message,
          type: 'chat',
          status: 'pending'
      };

      return api.execute<any>(
          '/api/V1/Request/Create',
          async () => {
              // Local Mock Logic
              return {
                  id: Date.now().toString(),
                  role: 'assistant',
                  content: 'Message processed locally',
                  timestamp: Date.now()
              };
          },
          { method: 'POST', body: payload }
      ).then(res => {
          // If server response (RequestOutputVM)
          if (res && res.result) {
              return {
                  id: res.id,
                  role: 'assistant',
                  content: res.result,
                  timestamp: new Date(res.completedAt || Date.now()).getTime()
              };
          }
          return res as Message;
      });
  },

  deleteConversation: async (id: string): Promise<boolean> => {
      // Server usually requires deleting requests by correlationId.
      // We assume a batch delete endpoint exists or we mock it locally.
      return api.execute<boolean>(
          `/api/V1/Request/Delete/${id}`, // Will delete individual request if ID matches, need bulk for conversation
          async () => {
            localDB.deleteItem('lahja_conversations', id, []);
            return true;
          },
          { method: 'DELETE' }
      );
  },

  clearAll: async (): Promise<boolean> => {
    return api.execute<boolean>(
      '/api/V1/Request/ClearAll', // Hypothetical endpoint
      async () => {
        localDB.set('lahja_conversations', []);
        return true;
      },
      { method: 'DELETE' }
    );
  }
};
